import { LowerCasePipe } from '@angular/common';
import type { Signal } from '@angular/core';
import { Component, computed, input, inject, ChangeDetectionStrategy } from '@angular/core';

import { MomentModule } from 'ngx-moment';

import type { CurrentUser } from '@core/account-types';
import { ActivityItemComponent } from '@core/activities-and-notifications-ui';
import type { TrackingActivity } from '@core/activities-types';
import type { FullCandidate } from '@core/candidate-types';
import { SourcesType } from '@core/candidate-types';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { CandidateSourcesContainerComponent } from '@core/tags-and-sources-legacy';
import { TimeAgoPipe } from '@core/time-legacy';
import {
  CollapsibleCardComponent,
  DividerComponent,
  HeadingComponent,
  RtTooltip,
  SourceNamePipe,
} from '@core/user-interface-legacy';

import { CandidateFieldRowSimpleComponent } from '../candidate-field-row-simple/candidate-field-row-simple.component';
import { CollapsibleCardAnalyticsDirective } from '../collapsible-card/collapsible-card-analytics.directive';

type ApplyLanguage = {
  hasLanguages: boolean;
  desc: string;
  tooltip: string | null;
};

@Component({
  selector: 'rt-candidate-details-card',
  imports: [
    CollapsibleCardComponent,
    HeadingComponent,
    TranslatePipe,
    CandidateFieldRowSimpleComponent,
    LowerCasePipe,
    TimeAgoPipe,
    MomentModule,
    RtTooltip,
    DividerComponent,
    SourceNamePipe,
    CandidateSourcesContainerComponent,
    ActivityItemComponent,
    CollapsibleCardAnalyticsDirective,
  ],
  templateUrl: './candidate-details-card.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './candidate-details-card.component.less',
})
export class CandidateDetailsCardComponent {
  private readonly i18n = inject(I18nService);

  public readonly CandidateSource = SourcesType;

  public candidate = input.required<FullCandidate>();
  public currentAdmin = input.required<CurrentUser>();
  public lastActivity = input<TrackingActivity | null>(null);
  public isMultilangEnabled = input<boolean>(false);
  public canManageCandidates = input<boolean>(false);
  public hasWorkflowAutomations = input<boolean>(false);
  public applyLanguage: Signal<ApplyLanguage | null> = computed(() => {
    if (!this.isMultilangEnabled()) return null;

    const placementLanguages = Array.from(
      new Set(
        this.candidate()
          .placements!.filter(p => p.language)
          .map(p => p.language.name),
      ),
    );

    return {
      hasLanguages: !!placementLanguages.length,
      desc:
        placementLanguages.length > 1
          ? this.i18n.translate('app.candidate.languages.multi', {
              language: placementLanguages[0],
              languagesCount: placementLanguages.length - 1,
            })
          : this.i18n.translate('app.candidate.languages.single', {
              language: placementLanguages[0],
            }),
      tooltip: placementLanguages.length > 1 ? placementLanguages.join(', ') : null,
    };
  });

  public candidateReferrers = computed(() => {
    const referrers = Array.from(
      new Set(
        this.candidate().referralReferrers.map(referrer =>
          !referrer || referrer.anonymizedAt
            ? this.i18n.translate('app.candidate.referred.deleted_referrer')
            : `${referrer.firstName} ${referrer.lastName}`,
        ),
      ),
    );

    const [firstReferrer, ...restReferrers] = referrers;

    return {
      hasReferrers: !!firstReferrer,
      desc:
        restReferrers.length > 0
          ? this.i18n.translate('app.candidate.referred.multiple', {
              firstReferrer,
              referrersCount: restReferrers.length,
            })
          : this.i18n.translate('app.candidate.referred.single', {
              firstReferrer,
            }),
      tooltip: restReferrers.length > 0 ? restReferrers.join(', ') : null,
    };
  });
}
